import React, { useState, useEffect } from "react";
import AdminLayout from "./AdminLayout";
import "./AdminForm.css";
import { API_BASE_URL } from "./apiConfig";

const USER_API = `${API_BASE_URL}/user`;

const CreateUser = () => {
  const [activeTab, setActiveTab] = useState("entry");
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });

  const [userData, setUserData] = useState([]);
  const [isFetching, setIsFetching] = useState(true);
  const [roles, setRoles] = useState(["Admin", "User"]);

  const emptyForm = {
    Client_ID: "",
    User_ID: "",
    User_Name: "",
    Password: "",
    ConfirmPassword: "",
    Role: "User",
    Company_ID: "1",
  };
  const [formData, setFormData] = useState(emptyForm);

  const loadRecords = async () => {
    setIsFetching(true);
    try {
      const res = await fetch(USER_API);
      if (!res.ok) throw new Error("Failed to fetch user records.");
      const data = await res.json();
      setUserData(data);
    } catch (err) {
      console.error("Load Error:", err);
      setMessage({ type: "error", text: err.message });
    } finally {
      setIsFetching(false);
    }
  };

  // Pull the same role list used in Role & Permission Management, so the two stay in sync
  const loadRoles = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/permission/roles`);
      const data = await res.json();
      if (data.roles && data.roles.length > 0) setRoles(data.roles);
    } catch (err) {
      console.error("Could not load roles, using defaults.", err);
    }
  };

  useEffect(() => {
    loadRecords();
    loadRoles();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage({ type: "", text: "" });

    if (formData.Password !== formData.ConfirmPassword) {
      setMessage({ type: "error", text: "Password and Confirm Password do not match." });
      return;
    }
    if (formData.Password.length < 4) {
      setMessage({ type: "error", text: "Password must be at least 4 characters." });
      return;
    }

    setIsLoading(true);

    const payload = {
      Client_ID: Number(formData.Client_ID),
      User_ID: Number(formData.User_ID),
      User_Name: formData.User_Name.trim(),
      Password: formData.Password,
      Role: formData.Role,
      Company_ID: Number(formData.Company_ID) || 1,
    };

    try {
      const response = await fetch(USER_API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = `Server Error: ${response.status} ${response.statusText}`;
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.message || errorData.error || errorMessage;
        } catch {
          if (errorText) errorMessage = `Server responded with: ${errorText.substring(0, 150)}`;
        }
        throw new Error(errorMessage);
      }

      setMessage({ type: "success", text: "User created successfully." });
      setFormData(emptyForm);
      loadRecords();
      setActiveTab("view");
    } catch (err) {
      console.error("API POST Error:", err);
      setMessage({
        type: "error",
        text: err.message === "Failed to fetch"
          ? "Network error: check the backend is running and reachable."
          : err.message,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AdminLayout>
      <div className="page-header">
        <h1 className="page-title">User Management</h1>
      </div>

      <div className="content-card">
        <div className="tab-container">
          <button
            className={`tab-btn ${activeTab === "entry" ? "active" : ""}`}
            onClick={() => setActiveTab("entry")}
          >
            Create New User
          </button>
          <button
            className={`tab-btn ${activeTab === "view" ? "active" : ""}`}
            onClick={() => setActiveTab("view")}
          >
            View Records ({userData.length})
          </button>
        </div>

        {activeTab === "entry" && (
          <div className="form-wrapper">
            {message.text && (
              <div className={`alert ${message.type === "success" ? "alert-success" : "alert-error"}`}>
                {message.text}
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div className="form-grid">
                <div className="form-group">
                  <label>Client ID *</label>
                  <input
                    name="Client_ID"
                    type="number"
                    className="form-control"
                    placeholder="e.g., 1"
                    value={formData.Client_ID}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>User ID *</label>
                  <input
                    name="User_ID"
                    type="number"
                    className="form-control"
                    placeholder="e.g., 1"
                    value={formData.User_ID}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>User Name *</label>
                  <input
                    name="User_Name"
                    className="form-control"
                    placeholder="e.g., John Doe"
                    value={formData.User_Name}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Company ID</label>
                  <input
                    name="Company_ID"
                    type="number"
                    className="form-control"
                    placeholder="e.g., 1"
                    value={formData.Company_ID}
                    onChange={handleChange}
                  />
                </div>
                <div className="form-group">
                  <label>Role *</label>
                  <select name="Role" className="form-control" value={formData.Role} onChange={handleChange} required>
                    {roles.map((r) => (
                      <option key={r} value={r}>{r}</option>
                    ))}
                  </select>
                </div>
                <div></div>
                <div className="form-group">
                  <label>Password *</label>
                  <input
                    name="Password"
                    type="password"
                    className="form-control"
                    placeholder="At least 4 characters"
                    value={formData.Password}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Confirm Password *</label>
                  <input
                    name="ConfirmPassword"
                    type="password"
                    className="form-control"
                    placeholder="Re-enter password"
                    value={formData.ConfirmPassword}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>

              <button type="submit" className="btn-submit" disabled={isLoading}>
                {isLoading ? "Saving..." : "Save User"}
              </button>
            </form>
          </div>
        )}

        {activeTab === "view" && (
          <div className="table-wrap">
            {isFetching ? (
              <div className="empty-state">Loading records...</div>
            ) : (
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Client ID</th>
                    <th>User ID</th>
                    <th>User Name</th>
                    <th>Role</th>
                    <th>Company ID</th>
                  </tr>
                </thead>
                <tbody>
                  {userData.length > 0 ? (
                    userData.map((item, idx) => (
                      <tr key={item.id || idx}>
                        <td>{item.client_ID ?? item.Client_ID ?? "-"}</td>
                        <td>{item.user_ID ?? item.User_ID ?? "-"}</td>
                        <td><strong>{item.user_Name ?? item.User_Name ?? "-"}</strong></td>
                        <td>{item.role ?? item.Role ?? "-"}</td>
                        <td>{item.company_ID ?? item.Company_ID ?? "-"}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="5" className="empty-state">
                        <div style={{ marginBottom: "8px", fontSize: "1.2rem" }}>👤</div>
                        No User records found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default CreateUser;